import { useMemo } from 'react';
import { type PartnerTreeNodeV2 } from '@sr/types';
import { useProfileV2 } from '#lib/Profile';
import { useLocations } from '../Locations/useLocations';
import { partnerTreeSearch } from '../utils/partnerTreeSearch';
import { useFlag } from './useFlag';

const hasRnpLocation = (partnerTrees: PartnerTreeNodeV2[], businessPartners?: string[]) =>
  partnerTreeSearch(partnerTrees, (item) => {
    if (businessPartners) {
      return (businessPartners.includes(item.dwKey) && item.hasRnpAccess) ?? false;
    }

    return item.hasRnpAccess ?? false;
  });

export const useHasRnpAccess = (businessPartners?: string[]) => {
  const showRequestNewProjectFlag = useFlag('show-request-new-project');
  const rnpNewAccessRoleFlag = useFlag('rnp-new-access-role');
  const locations = useLocations();
  const rnpRoleLocations = useLocations({ filterRnpRoleAccess: true });
  const { profile } = useProfileV2();

  const isSuperUser = !!profile?.aggregatedPermissions.roles.superUser;
  const isAdmin = !!profile?.aggregatedPermissions.roles.externalAdmin || isSuperUser;

  const locationHasRnpAccess = useMemo(
    () => hasRnpLocation(locations.partnerTrees, businessPartners),
    [locations.partnerTrees, businessPartners],
  );

  const rnpRoleLocationHasRnpAccess = useMemo(
    () => hasRnpLocation(rnpRoleLocations.partnerTrees, businessPartners),
    [rnpRoleLocations.partnerTrees, businessPartners],
  );

  if (!showRequestNewProjectFlag) {
    return false;
  }

  if (rnpNewAccessRoleFlag) {
    return rnpRoleLocationHasRnpAccess || (isSuperUser && locationHasRnpAccess);
  }

  return locationHasRnpAccess && isAdmin;
};
